import {
  APP_NAME,
  CONSENT_PARTS,
  CONSENT_VERSION,
  JOIN_BUTTON,
  type JoinResult,
  type JoinView,
  MAX_CONTACT_NAME,
  REQUEST_BUTTON,
} from '@eventping/shared'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { BellRing, Link2Off, MailCheck, MessageSquareText } from 'lucide-react'
import { type FormEvent, useEffect, useRef, useState } from 'react'
import { Card, FieldError, Footer, Page, Wordmark } from '@/components/shell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ApiFailure, api, useMe } from '@/lib/api'
import { cn } from '@/lib/utils'

/**
 * The join page (/join/<code>): the only way anyone starts getting updates. On a public event
 * (or a personal link) it subscribes at once; on a private event's shared link it is a request
 * the creator approves. The person gives their name and an email address (required) and,
 * optionally, a mobile number for texts; a number (offered only while the event has SMS seats)
 * needs the unchecked SMS consent box (packages/shared/src/consent.ts) ticked, and the server
 * refuses it otherwise. No sign-in, no analytics. docs/MESSAGING-COMPLIANCE.md and /opt-in describe this page.
 *
 * Texts-off mode (JoinView.textsOff): email only, no phone field or SMS consent; the API
 * refuses a number. The demo page always shows the full form: it is the opt-in proof.
 *
 * When the API has a Turnstile site key (MeResponse.turnstileSiteKey), this page, and only
 * this page, loads Cloudflare Turnstile and sends its token with the form. The demo page
 * (/join/demo) never does: it stores and sends nothing.
 */
export function JoinPage({ code }: { code: string }) {
  const view = useQuery({
    queryKey: ['join', code],
    queryFn: () => api<JoinView>(`/api/join/${code}`),
    retry: false,
  })
  const [result, setResult] = useState<JoinResult | null>(null)

  return (
    <Page header={<Wordmark />}>
      {view.isPending ? null : view.isError ? (
        <Inactive notFound={view.error instanceof ApiFailure && view.error.status === 404} />
      ) : result ? (
        <Done result={result} view={view.data} />
      ) : (
        <JoinForm code={code} view={view.data} onDone={setResult} />
      )}
      <Footer
        // The event's organizer decides (JoinView.textsOff), not the visitor (TEXTS_ALLOWLIST).
        texts={code === 'demo' || (view.data !== undefined && view.data.textsOff !== true)}
        note={
          <Link to="/recover" className="underline underline-offset-4">
            Already getting updates? Lost your link?
          </Link>
        }
      />
    </Page>
  )
}

function JoinForm({
  code,
  view,
  onDone,
}: {
  code: string
  view: JoinView
  onDone: (result: JoinResult) => void
}) {
  const requesting = view.mode === 'request'
  const me = useMe().data
  const signedIn = Boolean(me?.user)
  const siteKey = view.demo ? null : (me?.turnstileSiteKey ?? null)
  const [token, setToken] = useState<string | null>(null)
  const [resets, setResets] = useState(0)
  const [name, setName] = useState(view.name ?? '')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<{ field?: string; message: string } | null>(null)
  const textsOff = view.textsOff === true && !view.demo
  const wantsTexts = !textsOff && view.smsAvailable && phone.trim() !== ''

  useEffect(() => {
    if (view.name) setName(view.name)
  }, [view.name])

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!email.trim()) {
      setError({ field: 'email', message: 'Add your email address.' })
      return
    }
    if (wantsTexts && !agreed) {
      setError({
        field: 'consent',
        message: 'Tick the box to agree to get text updates, or leave the number blank.',
      })
      return
    }
    if (siteKey && !token) {
      setError({
        field: 'turnstile',
        message: 'Wait a moment for the check above the button to finish, then try again.',
      })
      return
    }
    setBusy(true)
    try {
      onDone(
        await api<JoinResult>(`/api/join/${code}`, {
          body: {
            name,
            email: email.trim() || undefined,
            phone: wantsTexts ? phone : undefined,
            consent: wantsTexts && agreed,
            consentVersion: CONSENT_VERSION,
            ...(siteKey && token ? { turnstileToken: token } : {}),
          },
        }),
      )
    } catch (err) {
      setError(
        err instanceof ApiFailure
          ? { field: err.field, message: err.message }
          : { message: 'Something went wrong. Try again.' },
      )
    } finally {
      setBusy(false)
      // A Turnstile token works once: get a fresh one for the next try.
      if (siteKey) {
        setToken(null)
        setResets((n) => n + 1)
      }
    }
  }

  return (
    <Card className="p-6">
      {view.demo && <DemoNote />}
      <p className="inline-flex items-center gap-1.5 text-sm font-medium text-primary">
        <BellRing className="size-4" aria-hidden /> Updates from {view.event.senderName}, sent by{' '}
        {APP_NAME}
      </p>
      <h1 className="mt-1 text-3xl font-semibold text-balance break-words">
        Get updates for {view.event.title}
      </h1>
      <p className="mt-2 text-lg">
        {textsOff
          ? 'No account or password needed. Updates come by email.'
          : 'No account or password needed. Just choose how you’d like to hear about each step.'}
      </p>
      <p className="mt-2 text-muted-foreground">
        You’ll get a short {textsOff ? 'email' : 'message'} as each step happens, with a link to a
        live checklist.
        {requesting && ` ${view.event.senderName} approves each request first.`}
      </p>

      <form className="mt-6 space-y-4" onSubmit={submit} noValidate>
        <div className="space-y-1.5">
          <Label htmlFor="join-name">Your name</Label>
          <Input
            id="join-name"
            value={name}
            maxLength={MAX_CONTACT_NAME}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
            required
            aria-invalid={error?.field === 'name' || undefined}
          />
          {error?.field === 'name' && <FieldError>{error.message}</FieldError>}
        </div>
        <fieldset className="space-y-4">
          <legend className="mb-3 font-medium" data-testid="channels-legend">
            {view.smsAvailable ? 'Email, and texts if you like' : 'Email'}
          </legend>
          <div className="space-y-1.5">
            <Label htmlFor="join-email">Email (required)</Label>
            <Input
              id="join-email"
              type="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              required
              aria-invalid={error?.field === 'email' || undefined}
            />
            {error?.field === 'email' && <FieldError>{error.message}</FieldError>}
          </div>

          {textsOff ? null : view.smsAvailable ? (
            <div className="space-y-1.5">
              <Label htmlFor="join-phone">Mobile number (optional, for texts)</Label>
              <Input
                id="join-phone"
                type="tel"
                inputMode="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 555-0123"
                autoComplete="tel-national"
                aria-describedby="join-phone-hint"
                aria-invalid={error?.field === 'phone' || undefined}
              />
              <p id="join-phone-hint" className="text-sm text-muted-foreground">
                A US mobile number.
              </p>
              {error?.field === 'phone' && <FieldError>{error.message}</FieldError>}
            </div>
          ) : (
            <p className="rounded-xl bg-muted px-4 py-3 text-sm" data-testid="sms-full">
              Text updates are full for this event. You’ll get every update by email.
            </p>
          )}
        </fieldset>

        {wantsTexts && (
          <div
            className={cn(
              'flex gap-3 rounded-2xl border p-4',
              error?.field === 'consent'
                ? 'border-destructive'
                : agreed && 'border-primary bg-accent',
            )}
          >
            <input
              id="join-consent"
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 size-6 shrink-0 accent-primary"
              aria-invalid={error?.field === 'consent' || undefined}
            />
            <label htmlFor="join-consent" className="text-[0.95rem] leading-relaxed">
              {CONSENT_PARTS.lead}
              <a href="/terms" target="_blank" rel="noopener" className="font-medium underline">
                {CONSENT_PARTS.terms}
              </a>
              {CONSENT_PARTS.and}
              <a href="/privacy" target="_blank" rel="noopener" className="font-medium underline">
                {CONSENT_PARTS.privacy}
              </a>
              {CONSENT_PARTS.end}
            </label>
          </div>
        )}
        {error?.field === 'consent' && <FieldError>{error.message}</FieldError>}
        {siteKey && <Turnstile siteKey={siteKey} resets={resets} onToken={setToken} />}
        {error?.field === 'turnstile' && <FieldError>{error.message}</FieldError>}
        {error && !error.field && <FieldError>{error.message}</FieldError>}

        <Button type="submit" size="lg" className="w-full text-base" disabled={busy}>
          {requesting ? REQUEST_BUTTON : JOIN_BUTTON}
        </Button>
        <p className="text-center text-sm text-muted-foreground">
          {requesting
            ? 'Nothing is sent until your request is approved. Then you get one confirmation.'
            : 'We’ll send one confirmation. After that, you only hear from us when a step is done.'}{' '}
          Every email has an unsubscribe link.
        </p>
        {signedIn && (
          <p className="text-center text-xs text-muted-foreground" data-testid="signed-in-note">
            Signed in? This event will also appear on your home page if you use your account’s
            email.
          </p>
        )}
      </form>
    </Card>
  )
}

function Done({ result, view }: { result: JoinResult; view: JoinView }) {
  const where = [result.emailMasked, result.phoneMasked].filter(Boolean).join(' and ')
  const title =
    result.status === 'requested'
      ? 'Request sent'
      : result.status === 'already'
        ? 'You already get these updates'
        : 'You’ll get updates'
  const Icon = result.status === 'requested' ? MailCheck : MessageSquareText
  return (
    <Card className="p-6 text-center">
      {view.demo && <DemoNote />}
      <Icon className="mx-auto mb-3 size-10 text-done" aria-hidden />
      <h1 className="text-2xl font-semibold">{title}</h1>
      <p className="mt-2 text-lg break-words">{view.event.title}</p>
      <p className="mt-3 text-muted-foreground">
        {result.status === 'requested'
          ? `${view.event.senderName} will review your request. Once it’s approved, we’ll send a confirmation to ${where}. Nothing is sent before then.`
          : result.status === 'already'
            ? view.textsOff
              ? 'Nothing new to send. Use the link in any update email to open the updates page.'
              : `Nothing new to send. Reply LINK to any ${APP_NAME} text, or use the link in any update email, to open the updates page.`
            : view.demo
              ? `On a real event's join link, we'd now send one confirmation to ${where}, with a link to the live updates page. This demo stored and sent nothing.`
              : `We just sent a confirmation to ${where}, with a link to the live updates page.`}
      </p>
      {result.phoneMasked && (
        <p className="mt-3 text-sm text-muted-foreground">
          Message frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for
          help.
        </p>
      )}
    </Card>
  )
}

/** On the demo page (/join/demo) only: what it is, and that nothing is kept. */
function DemoNote() {
  return (
    <p
      data-testid="demo-note"
      className="mb-4 rounded-xl bg-muted px-4 py-3 text-left text-sm text-muted-foreground"
    >
      <strong className="font-semibold text-foreground">Demo.</strong> This is {APP_NAME}’s real
      join form for a sample event, so you can see how people opt in. Nothing you enter here is
      stored, and nothing is sent.
    </p>
  )
}

function Inactive({ notFound }: { notFound: boolean }) {
  return (
    <Card className="text-center">
      <Link2Off className="mx-auto mb-3 size-10 text-muted-foreground" aria-hidden />
      <h1 className="text-xl font-semibold">
        {notFound ? 'This join link isn’t active' : 'Something went wrong'}
      </h1>
      <p className="mt-2 text-muted-foreground">
        {notFound
          ? 'It may have been replaced or turned off, or the event has ended. Ask the person who shared it for a new link.'
          : 'Please try again in a moment.'}
      </p>
    </Card>
  )
}

type TurnstileApi = {
  render(
    el: HTMLElement,
    opts: {
      sitekey: string
      action: string
      theme: 'auto'
      size: 'flexible'
      callback: (token: string) => void
      'expired-callback': () => void
      'error-callback': () => void
    },
  ): string
  reset(id: string): void
  remove(id: string): void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

const TURNSTILE_SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
let turnstileLoading: Promise<TurnstileApi> | null = null

/** Loads Cloudflare's script once, on the first join page that needs it. */
function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile)
  turnstileLoading ??= new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = TURNSTILE_SCRIPT
    script.async = true
    script.onload = () =>
      window.turnstile ? resolve(window.turnstile) : reject(new Error('no turnstile'))
    script.onerror = () => {
      turnstileLoading = null
      script.remove()
      reject(new Error('turnstile did not load'))
    }
    document.head.append(script)
  })
  return turnstileLoading
}

/**
 * Cloudflare Turnstile: usually invisible work, sometimes a checkbox. `onToken` gets the token
 * (or null when it expires or fails); bumping `resets` asks for a fresh one.
 */
function Turnstile({
  siteKey,
  resets,
  onToken,
}: {
  siteKey: string
  resets: number
  onToken: (token: string | null) => void
}) {
  const box = useRef<HTMLDivElement>(null)
  const widget = useRef<{ api: TurnstileApi; id: string } | null>(null)
  const report = useRef(onToken)
  report.current = onToken
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let gone = false
    loadTurnstile()
      .then((api) => {
        if (gone || !box.current) return
        const id = api.render(box.current, {
          sitekey: siteKey,
          action: 'join',
          theme: 'auto',
          size: 'flexible',
          callback: (token) => report.current(token),
          'expired-callback': () => report.current(null),
          'error-callback': () => report.current(null),
        })
        widget.current = { api, id }
      })
      .catch(() => {
        if (!gone) setFailed(true)
      })
    return () => {
      gone = true
      if (widget.current) widget.current.api.remove(widget.current.id)
      widget.current = null
    }
  }, [siteKey])

  useEffect(() => {
    if (resets > 0 && widget.current) widget.current.api.reset(widget.current.id)
  }, [resets])

  return (
    <div data-testid="turnstile">
      <div ref={box} className="min-h-[65px]" />
      {failed && (
        <FieldError>
          The bot check didn’t load. Check your connection, turn off any blocker for this page, and
          reload.
        </FieldError>
      )}
    </div>
  )
}
